import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import BlocksRenderer from "../BlocksRenderer";
import "./styles/PostFeed.css";

/**
 * The post grid + overlay, ported 1:1 from MitgestaltenUnterstuetzenPage.
 * Markup, classes and behaviour are unchanged — only the prefix moved
 * from .mu- to .rs-.
 *
 * What's configurable:
 *   category    filter tag; only entries with this category are shown
 *   limit       max number of cards (e.g. 3)
 *   queryParam  overlay URL param, so two feeds on one page don't clash
 *
 * <PostFeed
 *   entries={entries}
 *   category={page?.carouselCategory || "Begabungsförderung"}
 *   limit={3}
 *   queryParam="projekt"
 * />
 */
export default function PostFeed({
  entries = [],
  category,
  limit,
  queryParam = "beitrag",
  loading = false,
  error = null,
  emptyText,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filtered = category ? entries.filter((e) => e.category === category) : entries;
  const visible = limit ? filtered.slice(0, limit) : filtered;

  const openId = searchParams.get(queryParam);
  const openEntry =
    filtered.find((e) => String(e.id) === openId || e.documentId === openId) || null;

  const openById = (entry) => setSearchParams({ [queryParam]: entry.documentId ?? entry.id });
  const close = () => setSearchParams({});

  const empty =
    emptyText ??
    (category
      ? `Noch keine Beiträge mit der Kategorie „${category}“ markiert.`
      : "Noch keine Beiträge vorhanden.");

  return (
    <>
      {loading ? <p>Lädt…</p> : null}
      {error ? <p>Inhalt konnte nicht geladen werden.</p> : null}

      <div className="rs-posts-grid">
        {visible.map((entry) => (
          <article className="rs-post-card" key={entry.id}>
            <div className="rs-post-card__media">
              <img src={entry.image} alt={entry.title} loading="lazy" />
            </div>
            <div className="rs-post-card__body">
              <div className="rs-meta">
                {entry.category ? <span className="rs-pill">{entry.category}</span> : null}
                {entry.date ? <span className="rs-date">{entry.date}</span> : null}
              </div>
              <h3 className="rs-h3">{entry.title}</h3>
              <p className="rs-muted rs-post-card__excerpt">{entry.excerpt}</p>
              <button
                className="rs-link rs-link--button"
                type="button"
                onClick={() => openById(entry)}
              >
                Weiterlesen <span aria-hidden="true">→</span>
              </button>
            </div>
          </article>
        ))}

        {!loading && !error && visible.length === 0 ? (
          <p className="rs-empty">{empty}</p>
        ) : null}
      </div>

      {openEntry ? <PostOverlay entry={openEntry} onClose={close} /> : null}
    </>
  );
}

export function PostOverlay({ entry, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    // Lock the page behind the overlay. Plain `overflow: hidden` on the
    // body isn't enough on iOS Safari — a touch-drag on the backdrop
    // still scrolls the page underneath. Pinning the body at its
    // current scroll position with position:fixed stops that, and we
    // scroll back to exactly where we were on close.
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      window.removeEventListener("keydown", onKey);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [onClose]);

  return (
    <div className="rs-overlay" role="dialog" aria-modal="true" aria-label={entry.title}>
      <button className="rs-overlay__backdrop" aria-label="Schließen" onClick={onClose} />
      <article className="rs-overlay__panel">
        <div className="rs-overlay__topbar">
          <div className="rs-meta">
            {entry.category ? <span className="rs-pill">{entry.category}</span> : null}
            {entry.date ? <span className="rs-date">{entry.date}</span> : null}
          </div>
          <button
            type="button"
            className="rs-overlay__close"
            onClick={onClose}
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {entry.image ? (
          <div className="rs-overlay__hero">
            <img src={entry.image} alt={entry.title} />
          </div>
        ) : null}

        <div className="rs-overlay__content">
          <h2 className="rs-overlay__title">{entry.title}</h2>
          <p className="rs-overlay__lead">{entry.excerpt}</p>

          <div className="rs-overlay__body">
            <BlocksRenderer content={entry.content} />
          </div>

          {entry.gallery?.length > 0 ? (
            <div className="rs-overlay__gallery" aria-label="Bildergalerie">
              {entry.gallery.map((src, i) => (
                <figure className="rs-overlay__galleryItem" key={i}>
                  <img src={src} alt={entry.title} loading="lazy" />
                </figure>
              ))}
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
}

/** Category pill + date, exported for reuse (Termine, Einblicke). */
export function Meta({ category, date }) {
  if (!category && !date) return null;

  return (
    <div className="rs-meta">
      {category ? <span className="rs-pill">{category}</span> : null}
      {date ? <span className="rs-date">{date}</span> : null}
    </div>
  );
}